import type { AvatarConfig } from '@perplexicon/validation';

import { AvatarRenderer } from '../../../components/index.js';
import { colorForSeed, initialsFor } from '../../../components/avatar-utils.js';
import { avatarFrameLayout, avatarFrameMetrics } from '../avatar-frame-layout.js';
import { resolveCosmeticAsset } from '../profile-assets.js';

/**
 * The large profile-hero avatar (APP_SPEC §13.2). Two independent layers that
 * are never flattened into one image:
 *   1. the player's own avatar (their `AvatarConfig`, or an initials fallback)
 *   2. the selected *avatar frame* cosmetic, drawn over it (`pointer-events:none`)
 *
 * The frame is a separate replaceable asset resolved from the user's chosen
 * `avatarFrameId`; a `null` selection resolves to the neutral placeholder.
 *
 * Framing is driven by the frame's measured *ring*, never by its image bounds
 * — see `avatar-frame-layout.ts` for why, and `pnpm assets:frames` for where
 * the measurements come from. All this component does with them is place two
 * absolutely positioned layers.
 */
export interface AvatarDisplayProps {
  userId: string;
  username: string;
  avatar: AvatarConfig | null;
  /** Selected avatar-frame cosmetic id, or `null` for none. */
  frameId: string | null;
  /** Rendered character size in px. Defaults to the desktop size. */
  size?: number;
}

export function AvatarDisplay({
  userId,
  username,
  avatar,
  frameId,
  size = 144,
}: AvatarDisplayProps) {
  const layout = avatarFrameLayout(avatarFrameMetrics(frameId), size);

  return (
    <div
      className="relative shrink-0 justify-self-center tablet:justify-self-start"
      style={{ width: layout.box, height: layout.box }}
    >
      <div
        className="absolute overflow-hidden rounded-full shadow-2 ring-1 ring-borderStrong/40"
        style={{
          width: size,
          height: size,
          left: layout.avatarLeft,
          top: layout.avatarTop,
        }}
      >
        {avatar ? (
          <AvatarRenderer config={avatar} size={size} loading="eager" className="h-full w-full" />
        ) : (
          <span
            aria-hidden="true"
            className="flex h-full w-full items-center justify-center font-display text-4xl font-semibold text-textOnDark"
            style={{ backgroundColor: colorForSeed(userId) }}
          >
            {initialsFor(username)}
          </span>
        )}
      </div>
      <img
        src={resolveCosmeticAsset('avatarFrame', frameId)}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute select-none [filter:drop-shadow(0_2px_6px_rgb(4_31_29/0.35))]"
        // Dimensions are inline, not Tailwind: `frameWidth`/`frameHeight`/
        // `frameLeft`/`frameTop` are computed per frame by
        // `avatarFrameLayout`, so there is no utility class to express them.
        // `maxWidth: 'none'` is load-bearing — the frame art is wider than the
        // box it is positioned in, and `styles/index.css`'s global
        // `img { max-width: 100% }` reset would otherwise shrink it.
        //
        // (This used to read `max-w-none` as a class and blame Tailwind v4 for
        // emitting `max-width:0`. The cause was ours: `spacing.none` fed
        // Tailwind's spacing namespace and overwrote the `none` CSS keyword.
        // Fixed in `packages/design-tokens/src/spacing.ts`; the inline value
        // stays because it belongs with the computed geometry above.)
        style={{
          width: layout.frameWidth,
          height: layout.frameHeight,
          left: layout.frameLeft,
          top: layout.frameTop,
          maxWidth: 'none',
        }}
        draggable={false}
      />
    </div>
  );
}
